import { fr } from "date-fns/locale/fr";
import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import moment from "moment";
import "moment/locale/fr";
import "moment/locale/fr-ca";
import React, { JSX, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useHistory } from "@core/routing/useHistory";
import styled, { css } from "styled-components";
import scheduledLiveLogoImage from "@assets/images/live/scheduled_live_logo.png";
import visioYellowImage from "@assets/images/live/visio_yellow.svg";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { useWorkspaces } from "@domain/group/workspace/hooks";
import { Livecourse, LivecourseType } from "@domain/livecourse/livecourse";
import { useIsMobile } from "@utils/hooks";
import { DefaultButton } from "@ui/components/buttons/defaultButton";
import { NextButton } from "@ui/components/buttons/nextButton";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { Primary } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { WorkspaceLabel } from "@ui/components/group/workspaceLabel";
import { AdvancedDatePicker } from "@ui/components/inputs/advancedTimePicker";
import { MaterialTextInput } from "@ui/components/inputs/materialTextInput";
import { RoundedTextArea } from "@ui/components/inputs/textInput";
import { Toggle } from "@ui/components/inputs/toggle";
import { Row, row, Spacer, stack } from "@ui/components/layout";
import { Page } from "@ui/components/page";
import { EditableProfileImage } from "@ui/components/profile/editableProfileImage";
import { Toolbar } from "@ui/components/toolbar";
import { WarningText } from "@ui/components/warningText";
import { desktop, gap, mobile } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { LivecourseDateInput } from "@ui/pages/livecourses/livecourseDateInput";
import { LivecourseEditorController } from "@ui/pages/livecourses/livecourseEditorController/LivecourseEditorController";
import { LivecourseEditorControllerContext } from "@ui/pages/livecourses/livecourseEditorController/LivecourseEditorControllerContext";

registerLocale("fr", fr);

moment.locale("fr");

interface GroupLivecoursesEditorProps extends React.HTMLAttributes<HTMLDivElement> {
	shareroomId: string;
	type?: LivecourseType;
	initialLivecourse: Livecourse;
}
export const GroupLivecoursesEditor: React.FC<GroupLivecoursesEditorProps> = ({
	shareroomId,
	type,
	initialLivecourse,
	...htmlProps
}) => {
	const profile = useCurrentProfile();
	const controller = useMemo(() => new LivecourseEditorController(initialLivecourse), [initialLivecourse]);
	const livecourse = useObservable(controller.livecourse);
	livecourse.organizationId = undefined;
	const { title, banner, description, startDate, endDate, postAutoPublish } = livecourse;
	const { result: shareroomworkspaces, loading } = useWorkspaces(shareroomId);
	const { livecourseService, trackingService } = useServices();
	const isMobile = useIsMobile();
	const history = useHistory();
	const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(loading ? undefined : shareroomworkspaces[0]?.id);
	useEffect(() => {
		if (!selectedWorkspaceId) setSelectedWorkspaceId(shareroomworkspaces[0]?.id);
		livecourse.workspaceId = selectedWorkspaceId;
		livecourse.type = type ?? LivecourseType.INSTANT;
	}, [selectedWorkspaceId, shareroomworkspaces, type]);

	const renderRows = (scheduled: boolean): JSX.Element =>
		loading ? (
			<></>
		) : (
			<FormBloc>
				<Row>
					<BigTextInput
						style={{ opacity: title.length > 1 ? 1 : 0.5 }}
						{...htmlProps}
						value={title}
						onChange={(v) => controller.setTitle(v)}
						placeholder={I18n.t("live.titleplaceholder") + "*"}
					/>
				</Row>
				{scheduled && (
					<AdvancedDatePicker
						type={"HOURS_RANGE"}
						minDate={new Date()}
						locale={"fr"}
						startDate={startDate}
						onChangeStart={(date: Date | null) => date && controller.setStartDate(date)}
						onChangeEnd={(date: Date | null) => date && controller.setEndDate(date)}
						customInput={
							<Bloc>
								<StyledInput value={"" + moment(startDate).format("LL")} small={true} />
							</Bloc>
						}
						customInputHStart={
							<Bloc>
								<StyledInput value={"" + moment(startDate).format("HH[h] mm")} small={true} />
							</Bloc>
						}
						customInputHEnd={
							<Bloc>
								<StyledInput value={"" + moment(endDate).format("HH[h] mm")} small={true} />
							</Bloc>
						}
					/>
				)}
				<Row>
					<TextSpace>{I18n.t("shareroom.workspaces.title")}</TextSpace>
				</Row>
				<StyledOverflowRow style={{ marginBottom: 0, marginTop: isMobile ? 7 : 13, paddingBottom: 5 }}>
					{shareroomworkspaces
						//.filter((org) => org.name === "GENERAL")
						.map((workspace, index) => (
							<Bloc style={{ marginRight: 30 }} key={index}>
								<StyledWorkspaceLabel
									selected={workspace.id === selectedWorkspaceId}
									onClick={() => setSelectedWorkspaceId(workspace.id)}
									workspace={workspace.name}
									small={true}
								/>
							</Bloc>
						))}
				</StyledOverflowRow>
				<Row style={{ marginBottom: 0 }}>
					<Bloc>
						<WarningText title={I18n.t("live.page.notificationwarning")} />
					</Bloc>
				</Row>

				<Row>
					<EditableProfileImage
						reverse={isMobile}
						iconOpacity={0.5}
						containerStyle={{
							background: "#FFFFFF;",
							border: "1px solid #EAEAEA;",
							borderRadius: "3px;",
							boxShadow: "none",
						}}
						fontStyle={{
							fontFamily: "Sora",
							fontStyle: "normal",
							fontWeight: "bold",
							fontSize: "12px",
							lineHeight: "15px",
							textAlign: "left",
							color: "#13131A;",
							marginRight: "15px",
						}}
						style={{
							width: isMobile ? "100%" : "304px",
							marginTop: isMobile ? 20 : 8,
							marginBottom: isMobile ? 26 : 30,
						}}
						height={80}
						type="banner"
						image={banner}
						label={I18n.t("course.editor.image.upload")}
						onUpload={(media) => controller.setBanner(media)}
						onDelete={() => controller.setBanner(undefined)}
						canBeEmpty
					/>
				</Row>
				<Row>
					<StyledDescriptionArea>
						<Row>
							<Bloc style={{ marginTop: 3, width: "-webkit-fill-available" }}>
								<DescriptionInput
									style={{ width: isMobile ? "175%" : "100%", height: "80px" }}
									rows={4}
									value={description || ""}
									onChange={(e) => controller.setDescription(e.target.value)}
									placeholder={I18n.t("organization.accreditation.description.placeholder")}
									autoFocus={false}
								/>
								<div
									style={{ textAlign: "end", width: isMobile ? "175%" : "100%" }}
								>{`${description?.length}/399`}</div>
							</Bloc>
						</Row>
					</StyledDescriptionArea>
				</Row>
				<Row style={{ margin: isMobile ? "21px 0px 12px 0px" : "31px 0px 12px 0px" }}>
					<Toggle toggled={postAutoPublish} onChange={(autopublish) => controller.setPostAutoPublish(autopublish)} />
					<Primary
						style={{
							fontFamily: "Sora",
							fontStyle: "normal",
							fontWeight: "normal",
							fontSize: "12px",
							lineHeight: "15px",
							color: " #000000",
							padding: !isMobile ? "4px 0px 0px 9px" : "0px 0px 0px 12px",
						}}
					>
						{I18n.t(`live.workspace.autopublish`)}
					</Primary>
				</Row>
			</FormBloc>
		);

	const handleTracking = (type?: LivecourseType): void => {
		if (type === LivecourseType.INSTANT) {
			trackingService.track("Classroom - create - live instantané - abandon");
		}

		if (type === LivecourseType.SCHEDULED) {
			trackingService.track("Classroom - create - live programmé - abandon");
		}
	};

	return !profile || loading ? (
		<></>
	) : (
		<LivecourseEditorControllerContext.Provider value={controller}>
			<MainContainer containerStyle={{ paddingTop: 0 }} {...htmlProps}>
				<ContentBis>
					{!isMobile && (
						<AlignStart>
							<GoBack
								onClick={() => {
									handleTracking(type);
									// if(type===LivecourseType.INSTANT){

									// 	trackingService.track("Classroom - create - live instantané - abandon");
									// }

									// if(type===LivecourseType.SCHEDULED){
									// 	trackingService.track("Classroom - create - live programmé - abandon");
									// }
									history.goBack();
								}}
							>
								<GoBackToShareroom>
									<NextButton bgColor={"white"} inverted />
									<div>{I18n.t("back")}</div>
								</GoBackToShareroom>
							</GoBack>
						</AlignStart>
					)}

					<Card>
						{!isMobile && (
							<Row style={{ marginBottom: 0 }}>
								{type === LivecourseType.INSTANT && (
									<Column>
										<LeftPanInstant style={{ minHeight: isMobile ? 0 : 557 }}>
											<img src={visioYellowImage} width={145} height={145} />
											<WhiteRectangle />
											<TextDescription>{I18n.t("live.presentation")}</TextDescription>
										</LeftPanInstant>
									</Column>
								)}
								{type === LivecourseType.SCHEDULED && (
									<Column>
										<LeftPanScheduled style={{ minHeight: isMobile ? 0 : 593 }}>
											<img src={scheduledLiveLogoImage} width={145} height={"auto"} />
											<WhiteRectangle style={{ marginTop: 75 }} />
											<TextDescription>{I18n.t("live.presentation")}</TextDescription>
										</LeftPanScheduled>
									</Column>
								)}
								{type === LivecourseType.INSTANT && renderRows(false)}
								{type === LivecourseType.SCHEDULED && renderRows(true)}
							</Row>
						)}
						{isMobile && (
							<>
								{type === LivecourseType.INSTANT && (
									<Column>
										<LeftPanInstant style={{ minHeight: isMobile ? 0 : 557 }}>
											<img src={visioYellowImage} width={70} height={70} />
											<TextDescription>{I18n.t("live.presentation")}</TextDescription>
										</LeftPanInstant>
									</Column>
								)}
								{type === LivecourseType.SCHEDULED && (
									<Column>
										<LeftPanScheduled style={{ minHeight: isMobile ? 0 : 557 }}>
											<img style={{ top: 56 }} src={scheduledLiveLogoImage} width={70} height={"auto"} />
											<TextDescription style={{ top: 60 }}>{I18n.t("live.presentation")}</TextDescription>
										</LeftPanScheduled>
									</Column>
								)}

								{type === LivecourseType.INSTANT && renderRows(false)}
								{type === LivecourseType.SCHEDULED && renderRows(true)}
							</>
						)}
					</Card>
				</ContentBis>

				<StyledToolbar>
					<Bottomnav>
						<Spacer />
						<Row>
							{isMobile && (
								<Bloc style={{ marginRight: 15, paddingRight: 15 }}>
									<DefaultButton
										onClick={() => {
											handleTracking(type);
											// if (type === LivecourseType.INSTANT) {
											// 	trackingService.track("Classroom - create - live instantané - abandon");
											// }

											// if (type === LivecourseType.SCHEDULED) {
											// 	trackingService.track("Classroom - create - live programmé - abandon");
											// }
											history.goBack();
										}}
										style={{ backgroundColor: "white" }}
									>
										{I18n.t("cancel")}
									</DefaultButton>
								</Bloc>
							)}
							<Bloc>
								<PrimaryButton
									onClick={async () => {
										try {
											if (type === LivecourseType.INSTANT) {
												controller.setInstantDates();
												// TODO add tracking;
												trackingService.track("Classroom - create - live instantané");
											}
											trackingService.track("Classroom - create - live programmé");
											const created = await livecourseService.saveLivecourse(
												type === LivecourseType.INSTANT ? livecourse : controller.livecourse.get()
											);
											createToast(I18n.t("success"), I18n.t("live.createsuccess"), "success");
											history.push(Path.LiveGroupInfos(created.id, shareroomId));
										} catch {
											createToast(I18n.t("error"), I18n.t("live.createfail"), "error");
										}
									}}
									disabled={!title}
								>
									{I18n.t("validate")}
								</PrimaryButton>
							</Bloc>
						</Row>
					</Bottomnav>
				</StyledToolbar>
			</MainContainer>
		</LivecourseEditorControllerContext.Provider>
	);
};

const GoBack = styled.div`
	width: 100%;
`;

const ContentBis = styled(RoundedBlock)`
	@media ${desktop} {
		padding: 0;
	}
	@media ${mobile} {
		padding: 0;
		//padding: 40px;
	}

	background-color: inherit;
	border: none;
	width: 100%;
	${gap(40)};
`;

export const Card = styled(RoundedBlock)`
	@media ${desktop} {
		${gap(80)};
		margin-left: 8%;
		border-radius: 21px;
	}

	@media ${mobile} {
		border-radius: 7px;
		background-color: #f3f4f6;
	}
`;

const MainContainer = styled(Page)<{ maxWidth?: number }>`
	${stack(40, "center", "center", undefined, true)};
	@media ${desktop} {
		padding-top: 0;
	}
`;

const DescriptionInput = styled(RoundedTextArea)<{ isInvalid?: boolean }>`
	width: 100%;
	padding: 10px 12px 9px;

	border-radius: 10px;
	font-size: 0.935rem;
	${({ isInvalid }) =>
		isInvalid &&
		css`
			border: 1px solid ${({ theme }) => theme.colors.red};
		`}
`;

const StyledInput = styled(LivecourseDateInput)<{ small?: boolean }>`
	max-width: 180px;
	font-family: Sora;
	font-style: normal;
	font-weight: 600;
	font-size: 12px;
	line-height: 15px;
	color: #000000;
	${({ small }) =>
		!small &&
		css`
			position: absolute;
			right: 5px;
		`}
`;

const Bloc = styled.div`
	position: relative;
	font-family: Sora;
	margin-top: 12px;
	${({ theme }) => theme.colors.primary};
	font-size: 13px;
	font-weight: 100;
`;

const StyledDescriptionArea = styled.div`
	@media ${desktop} {
		width: -webkit-fill-available;
	}
	@media ${mobile} {
		top: 363px;
		width: 85%;
	}
`;

const StyledWorkspaceLabel = styled(WorkspaceLabel)<{ small?: boolean }>`
	flex-shrink: 0;
	//max-width: 180px;

	${({ small }) =>
		!small &&
		css`
			position: absolute;
			right: 35px;
		`}
`;

const AlignStart = styled.div`
	align-self: flex-start;
	margin-bottom: 27px !important;
`;
const GoBackToShareroom = styled.div`
	${row(12, "flex-start", "center")};
	${({ theme }) => theme.textStyles.title};
`;

const TextSpace = styled.div`
	font-family: Sora;
	font-style: normal;
	font-weight: normal;
	font-size: 12px;
	line-height: 15px;
	color: #8b8b8b;

	@media ${desktop} {
		padding-top: 17px;
	}

	@media ${mobile} {
		padding-top: 17px;
		padding-bottom: 13px;
	}
`;

const BigTextInput = styled(MaterialTextInput)<{ maxWidth?: number }>`
	width: 100%;
	@media ${desktop} {
		font-family: Sora;
		font-style: normal;
		font-weight: 600;
		font-size: 16px;
		line-height: 40px;
		height: 44px;

		mix-blend-mode: normal;
		color: black;
		opacity: 0.5;
		///* or 250% */
		input {
			font-family: Sora;
			font-style: normal;
			font-weight: 600;
			font-size: 21px;
			line-height: 40px;
			color: black;
		}
		input:focus {
			color: black;
			opacity: 1;
		}
	}
	input::placeholder {
		font-size: 16px;
		color: #b2b2b2;
		opacity: 1;
		@media (max-width: 490px) {
			font-size: 12px;
		}
	}

	@media ${mobile} {
		font-family: Sora;
		font-style: normal;
		font-weight: 600;
		font-size: 16px;
		line-height: 40px;
		min-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px !important` : undefined)};
		max-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px !important` : undefined)};
		height: 44px;
		mix-blend-mode: normal;
		color: black;
		opacity: 0.5;
		///* or 250% */
		input {
			font-family: Sora;
			font-style: normal;
			font-weight: 600;
			font-size: 21px;
			line-height: 40px;
			color: black;
			min-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px !important` : undefined)};
			max-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px !important` : undefined)};
		}
		input:focus {
			color: black;
			opacity: 1;
			min-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px !important` : undefined)};
			max-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px !important` : undefined)};
		}
	}
	input::placeholder {
		min-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px !important` : undefined)};
		max-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px !important` : undefined)};
		font-size: 16px;
		color: #b2b2b2;
		opacity: 1;
		@media (max-width: 490px) {
			font-size: 12px;
		}
	}
`;

const WhiteRectangle = styled.div`
	@media ${desktop} {
		position: relative;
		width: 108px;
		height: 6px;
		left: 35px;
		border-radius: 15px;
		background: ${({ theme }) => theme.colors.white};
		max-width: 234px;
		margin-top: 26px;
	}
`;
const TextDescription = styled.div`
	@media ${desktop} {
		position: relative;
		flex-direction: column;
		left: 35px;
		font-size: 16px;
		color: #ffffff;
		max-width: 234px;
		font-weight: bold;
		text-align: left;
		margin-top: 63px;
		padding-right: 7px;
		font-size: 18px;
		font-family: Sora;
		font-style: normal;
		line-height: 30px;
	}

	@media ${mobile} {
		font-family: Sora;
		font-style: normal;
		font-weight: bold;
		font-size: 16px;
		line-height: 20px;
		color: #ffffff;
		position: absolute;
		top: 58px;
		left: 126px;
		max-width: 211px;
		text-align: left;
	}
`;

const Bottomnav = styled.div`
	@media ${desktop} {
		height: 68px;
		${row(12, "flex-end", "center")};
	}
	@media ${mobile} {
		${stack(10, "center", "center", "reverse")};
	}
`;

const LeftPanScheduled = styled.div`
	@media ${desktop} {
		min-height: 593px;
		width: 120%;
		height: 100%;
		background-color: ${({ theme }) => theme.colors.secondary};
		border-radius: 21px 0px 0px 21px;
		box-shadow: 0 4px 46px 0 rgba(0, 0, 0, 0.1);
		box-shadow: 0 4px 46px 0 rgb(0 0 0 / 10%);
		> img {
			position: relative;
			margin-top: 98px;
			left: 30px;
		}
	}

	@media ${mobile} {
		height: 157px;
		background-color: ${({ theme }) => theme.colors.secondary};
		border-radius: 7px 7px 7px 7px;
		box-shadow: 0 4px 46px 0 rgba(0, 0, 0, 0.1);
		> img {
			position: absolute;
			top: 43px;
			left: 31px;
		}
	}
`;

const LeftPanInstant = styled.div`
	@media ${desktop} {
		min-height: 537px;
		width: 120%;
		height: 100%;
		background-color: ${({ theme }) => theme.colors.primary};
		border-radius: 21px 0px 0px 21px;
		box-shadow: 0 4px 46px 0 rgba(0, 0, 0, 0.1);
		background-color: #e83636;
		box-shadow: 0 4px 46px 0 rgb(0 0 0 / 10%);
		> img {
			position: relative;
			margin-top: 71px;
			left: 30px;
		}
	}

	@media ${mobile} {
		height: 157px;
		background-color: ${({ theme }) => theme.colors.primary};
		box-shadow: 0 4px 46px 0 rgba(0, 0, 0, 0.1);
		border-radius: 7px 7px 7px 7px;
		> img {
			position: absolute;
			top: 43px;
			left: 31px;
		}
	}
`;

const StyledToolbar = styled(Toolbar)`
	height: 80px;
	position: fixed;
	display: -webkit-box;
	display: -webkit-flex;
	display: -ms-flexbox;
	display: -webkit-box;
	display: -webkit-flex;
	display: -ms-flexbox;
	display: flex;
	-webkit-box-pack: center;
	-webkit-justify-content: center;
	-ms-flex-pack: center;
	z-index: 10;
	-webkit-box-pack: center;
	-webkit-justify-content: center;
	-ms-flex-pack: center;
	justify-content: center;
	left: 0;
	bottom: 0;
	right: -281px;
	padding: 22px 100px;
	border-top-left-radius: 6px;
	border-top-right-radius: 6px;
	box-shadow: 0 2px 54px 0 rgb(0 0 0 / 12%);
	background-color: #ffffff;
	> * {
		flex: 1;
		max-width: 630px;
	}

	@media ${mobile} {
		height: 80px !important;
		padding: 0;
		right: 0px;
	}
	padding-top: 0;
`;

const Column = styled(Bloc)`
	margin-top: unset;
	border-radius: 21px 0px 0px 21px;
`;

const FormBloc = styled(Bloc)`
	@media ${desktop} {
		padding: 10px;
		padding-left: 72px;
		width: 70%;
	}

	@media ${mobile} {
		padding: 20px;
		padding-top: 0px;
		margin-top: 11px;
		border-radius: 7px;
		background-color: white;
	}
`;

const StyledOverflowRow = styled(Row)`
	overflow: auto;

	::-webkit-scrollbar {
		width: 8px;
		height: 4px;
	}

	/* Track */
	::-webkit-scrollbar-track {
		box-shadow: inset 0 0 5px grey;
		border-radius: 10px;
	}

	/* Handle */
	::-webkit-scrollbar-thumb {
		background: #007aff;
		border-radius: 10px;
	}

	/* Handle on hover */
	::-webkit-scrollbar-thumb:hover {
		background: #b30000;
	}
`;
